import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, AtSign, Mail, Lock } from 'lucide-react';
import { Logo } from '@/components/layout/Logo';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { getApiErrorMessage } from '@/api/client';
import { useSeo } from '@/hooks/useSeo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-zA-Z0-9_]{3,64}$/;

interface FormErrors {
  name?: string;
  username?: string;
  email?: string;
  password?: string;
  confirm?: string;
  agree?: string;
}

export function Register() {
  useSeo({ title: "Ro'yxatdan o'tish — SQLMaster.uz", path: '/register', noindex: true });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);
  const push = useToastStore((s) => s.push);

  const [form, setForm] = useState({ name: '', username: '', email: '', password: '', confirm: '' });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: FormErrors = {};
    if (!form.name.trim()) next.name = t('auth.errorRequired');
    if (!USERNAME_RE.test(form.username)) next.username = t('auth.errorUsername');
    if (!EMAIL_RE.test(form.email)) next.email = t('auth.errorEmail');
    if (form.password.length < 8) next.password = t('auth.errorPasswordShort');
    if (form.confirm !== form.password) next.confirm = t('auth.errorPasswordMatch');
    if (!agree) next.agree = t('auth.errorAgree');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    try {
      await register({
        email: form.email,
        username: form.username,
        password: form.password,
        displayName: form.name,
      });
      push(t('auth.codeSent'), 'success');
      // User hali yaratilmadi — email tasdiqlash sahifasiga o'tamiz
      navigate('/verify-email', { state: { email: form.email.trim().toLowerCase() } });
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const field = (
    key: keyof typeof form,
    labelKey: string,
    placeholderKey: string,
    Icon: typeof User,
    type = 'text',
    autoComplete?: string,
  ) => (
    <div>
      <label className="mb-1.5 block text-sm font-medium">{t(labelKey)}</label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
        <input
          type={type}
          value={form[key]}
          onChange={(e) => set(key, e.target.value)}
          placeholder={t(placeholderKey)}
          className="input pl-10"
          autoComplete={autoComplete}
        />
      </div>
      {errors[key] && <p className="mt-1 text-xs text-[var(--error)]">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 flex justify-center">
        <Logo />
      </div>

      <div className="card p-6 sm:p-8">
        <h1 className="mb-1 text-2xl font-bold">{t('auth.registerTitle')}</h1>
        <p className="mb-6 text-sm text-content-secondary">{t('auth.registerSubtitle')}</p>

        <form onSubmit={submit} noValidate className="space-y-4">
          {field('name', 'auth.name', 'auth.namePlaceholder', User, 'text', 'name')}
          {field('username', 'auth.username', 'auth.usernamePlaceholder', AtSign, 'text', 'username')}
          {field('email', 'auth.email', 'auth.emailPlaceholder', Mail, 'email', 'email')}
          {field('password', 'auth.password', 'auth.passwordPlaceholder', Lock, 'password', 'new-password')}
          {field('confirm', 'auth.confirmPassword', 'auth.passwordPlaceholder', Lock, 'password', 'new-password')}

          <div>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-content-secondary">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-line accent-[var(--brand-primary)]"
              />
              <span>{t('auth.agree')}</span>
            </label>
            {errors.agree && <p className="mt-1 text-xs text-[var(--error)]">{errors.agree}</p>}
          </div>

          <Button type="submit" loading={loading} className="w-full">
            {t('auth.registerButton')}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-content-secondary">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:underline">
            {t('auth.signinLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
